import React from "react";
import { Box, Typography, Switch, Radio, RadioGroup } from "@mui/material";
import { CustomAutoComplete } from "components/CustomComponents";
import { themes } from "services/constants";
import {
  type,
  SftDeductBreakPenaltyPolicyExample,
  breakOptions,
  boxStyle,
  CustomBox,
  optionsStyle,
  individualBoxStyle,
  infoMessageStyle,
  additionalInfoStyle,
  cardStyle,
  mainOptions,
} from "../ShiftCategoryOptions";

export const ApplyBreakPenaltyPolicy = ({
  sftPenaltyPolicy,
  watch,
  setValue,
  register,
  errors,
  resetField,
  handleDeductBreakPenaltyPolicy,
}) => {
  if (sftPenaltyPolicy) {
    return null;
  }

  return (
    <Box sx={mainOptions}>
      <Box sx={boxStyle(watch("is_apply_break_penalty_policy"))}>
        <Typography fontWeight="medium">Apply break penalty policy</Typography>
        <Switch
          checked={watch("is_apply_break_penalty_policy")}
          onChange={(event) => {
            const checked = event.target.checked;
            setValue("is_apply_break_penalty_policy", checked);
            if (!checked) {
              setValue("is_deduct_break_penalty_policy", "always");
              setValue("break_penalty_deduct_type", "");
              [
                "extra_break_minutes",
                "break_deduction",
                "additional_break_minutes",
                "additional_break_deduction",
              ].forEach((field) => {
                resetField(field, { keepError: false });
                setValue(field, 0, { shouldValidate: false });
              });
            }
          }}
        />
      </Box>
      {watch("is_apply_break_penalty_policy") && (
        <>
          <Box sx={{ ml: 2, mb: 2 }}>
            <Box>
              <Typography fontWeight="medium">Break</Typography>
              <Typography variant="body2" color="text.secondary">
                Extra breaks taken during shift time are deducted from payable
                hours.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2 }}>
            <RadioGroup
              row
              value={watch("is_deduct_break_penalty_policy")}
              onChange={handleDeductBreakPenaltyPolicy}
              sx={optionsStyle}
            >
              {breakOptions.map((opt) => {
                const isActive =
                  watch("is_deduct_break_penalty_policy") === opt.id;
                return (
                  <Box key={opt.id} sx={cardStyle(isActive)}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Radio value={opt.id} />
                      <Typography
                        fontWeight="medium"
                        sx={{
                          color: isActive ? themes.stepActiveColor : "inherit",
                        }}
                      >
                        {opt.label}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 4, maxWidth: 250, wordBreak: "break-word" }}
                    >
                      {opt.description}
                    </Typography>
                  </Box>
                );
              })}
            </RadioGroup>
          </Box>
          <Box sx={additionalInfoStyle}>
            <Box sx={individualBoxStyle}>
              <Typography variant="body2" color="text.secondary">
                An extra break of up to
              </Typography>
              <CustomBox
                name="extra_break_minutes"
                type="minutes"
                value={watch("extra_break_minutes")}
                register={register}
                errors={errors}
                handleChange={(e, value) => {
                  setValue("extra_break_minutes", value);
                }}
                rules={{ required: "Enter value" }}
                inputWidth="50px"
              />

              <Typography variant="body2" color="text.secondary">
                minutes will result in a
              </Typography>
              <CustomBox
                name="break_deduction"
                type="minutes"
                value={watch("break_deduction")}
                register={register}
                errors={errors}
                handleChange={(e, value) => {
                  setValue("break_deduction", value);
                }}
                rules={{ required: "Enter value" }}
                inputWidth="50px"
              />

              <Typography variant="body2" color="text.secondary">
                minute
              </Typography>
              <Typography variant="body2" color="text.secondary">
                deduction
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  width: 180,
                  verticalAlign: "middle",
                }}
              >
                <CustomAutoComplete
                  dataList={type
                    ?.filter((obj) => obj.label !== "Individual OT")
                    .map((obj) => ({
                      ...obj,
                      label: obj.label,
                    }))}
                  rules={{
                    ...register("break_penalty_deduct_type", {
                      required: false,
                      validate: {
                        minLengthNoSpaces: () => {
                          if (
                            watch("is_apply_break_penalty_policy") &&
                            watch("break_penalty_deduct_type") === ""
                          ) {
                            return "Value is required";
                          }
                          return true;
                        },
                      },
                    }),
                  }}
                  selectedValue={watch("break_penalty_deduct_type")}
                  updateDataFunction={(event, data) =>
                    setValue("break_penalty_deduct_type", data?.label)
                  }
                  placeHolder="Select type"
                  errorMessage={
                    errors?.break_penalty_deduct_type?.message
                      ? errors?.break_penalty_deduct_type?.message
                      : ""
                  }
                  textBoxError={errors?.break_penalty_deduct_type ? true : false}
                />
              </Box>
            </Box>
          </Box>
          <Box sx={additionalInfoStyle}>
            <Box sx={individualBoxStyle}>
              <Typography variant="body2" color="text.secondary">
                For every additional
              </Typography>
              <CustomBox
                name="additional_break_minutes"
                type="minutes"
                value={watch("additional_break_minutes")}
                register={register}
                errors={errors}
                handleChange={(e, value) => {
                  setValue("additional_break_minutes", value);
                }}
                rules={{ required: "Enter value" }}
                inputWidth="50px"
              />

              <Typography variant="body2" color="text.secondary">
                minutes beyond that, another
              </Typography>
              <CustomBox
                name="additional_break_deduction"
                type="minutes"
                value={watch("additional_break_deduction")}
                register={register}
                errors={errors}
                handleChange={(e, value) => {
                  setValue("additional_break_deduction", value);
                }}
                rules={{ required: "Enter value" }}
                inputWidth="50px"
              />

              <Typography variant="body2" color="text.secondary">
                {`minute deduction will be applied ${watch(
                  "break_penalty_deduct_type",
                )}`}
              </Typography>
            </Box>
          </Box>

          <Box sx={infoMessageStyle}>
            <SftDeductBreakPenaltyPolicyExample />
          </Box>
        </>
      )}
    </Box>
  );
};
